import React, { useState, useEffect, useMemo } from "react";
import { renderToString } from "react-dom/server";

import {
  MapContainer,
  TileLayer,
  LayersControl,
  GeoJSON,
  LayerGroup,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Select, Form, Row, Col, Card, Button, Spin } from "antd";
import { ReloadOutlined, AlertOutlined } from "@ant-design/icons";

// Importa las imágenes desde node_modules
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import "./EstilosGeovisor.css";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";

import {
  styleDepartamentos,
  styleMunicipios,
  styleresguardoIndigenaFormalizado,
  stylezonaReservaCampesinaConstituida,
  styleconsejosComunitarios,
  styleVisitasPatti,
  stylereservaForestalNacional,
  stylerunap,
  styledesagregacionRegional,
  styleGeoJSONNumeroVisita,
} from "./mapStyles";
import getVisitasTecnicasGeovisor, {
  getDataGeovisorArn,
} from "../../services/geovisor/getVisitasTecnicasGeovisor";

const { BaseLayer, Overlay } = LayersControl;
const colombiaBounds = [
  [-4.231, -79.03],
  [12.437, -66.758],
];

// Merge para que L.Icon.Default conozca dónde están
L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
});
const colorPorNumeroVisita = {
  1: "#8e7cc3", // violeta frío
  2: "#6aa84f", // verde fuerte
  3: "#f6b26b", // naranja fuerte
  4: "#c19a6b", // caramelo tostado (reemplazo del rojo oscuro)
  5: "#c27ba0", // violeta fuerte
  6: "#ffd966", // amarillo fuerte
  7: "#a4a4a4", // gris medio
  8: "#444444", // gris oscuro
  9: "#e691b2", // rosado fuerte
  10: "#93c47d", // verde medio
  11: "#ffe599", // amarillo medio
  12: "#ff6f00", // naranja quemado (reemplazo del azul intenso)
  13: "#e69138", // naranja intenso
  14: "#a64d79", // púrpura fuerte
  15: "#76a5af", // cian fuerte
};

const trianguloIcono = L.divIcon({
  html: renderToString(
    <div className="triangulo-custom">
      <AlertOutlined style={{ color: "#8d6e63", fontSize: "14px" }} />
    </div>
  ),
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 30], // para que la punta del triángulo esté justo donde es el punto
});

const MarcadorPersonalizado = (color = "#1890ff") => {
  return L.divIcon({
    html: renderToString(
      <div className="marcador-custom" style={{ backgroundColor: color }}></div>
    ),
    className: "",
    iconSize: [30, 42],
    iconAnchor: [15, 42],
  });
};

export default function GeovisorArn() {
  const [loading, setLoading] = useState(true);
  const [dataVisitasTecnicasGeovisor, setDataVisitasTecnicasGeovisor] =
    useState(null);

  const [consejosComunitarios, setConsejosComunitarios] = useState(null);
  const [zonaReservaCampesinaConstituida, setZonaReservaCampesinaConstituida] =
    useState(null);
  const [resguardoIndigenaFormalizado, setResguardoIndigenaFormalizado] =
    useState(null);
  const [reservaForestalNacional, setReservaForestalNacional] = useState(null);
  const [runap, setRunap] = useState(null);
  const [desagregacionRegional, setDesagregacionRegional] = useState(null);
  const [sostenibilidad, setSostenibilidad] = useState(null);
  const [mostrarSostenibilidad, setMostrarSostenibilidad] = useState(false);

  const [dataOriginal, setDataOriginal] = useState(null); // Datos originales sin filtrar

  const [filtroDepartamento, setFiltroDepartamento] = useState("");
  const [filtroMunicipio, setFiltroMunicipio] = useState("");
  const [filtroCodigoSIRR, setFiltroCodigoSIRR] = useState("");
  // Cambia el estado para selección múltiple
  const [filtroNumerovisita, setFiltroNumerovisita] = useState([]);

  const [departamentosUnicos, setDepartamentosUnicos] = useState([]);
  const [municipiosUnicos, setMunicipiosUnicos] = useState([]);
  const [codigosSIRRUnicos, setCodigosSIRRUnicos] = useState([]);
  const [numeroVisitaUnicas, setNumeroVisitaUnicas] = useState([]);

  //Control de Permisos
  const appName = "mapa-arn"; // El nombre de la aplicación actual
  const permissions = useSelector((state) =>
    selectAppPermissions(state, appName)
  );

  const [capas, setCapas] = useState({});

  const cargarCapa = async (key, url) => {
    if (capas[key]) return; // ✅ ya cargada en cache, no repitas fetch

    try {
      const resp = await fetch(url);
      const data = await resp.json();

      setCapas((prev) => ({ ...prev, [key]: data }));
    } catch (error) {
      console.error("Error cargando capa:", key, error);
    }
  };

  // Municipios únicos según el departamento seleccionado
  const municipiosFiltrados = useMemo(() => {
    const features = dataOriginal?.features || [];
    return [
      ...new Set(
        features
          .filter(
            (f) =>
              !filtroDepartamento ||
              f.properties.departamento === filtroDepartamento
          )
          .map((f) => f.properties.municipio)
          .filter(Boolean)
      ),
    ];
  }, [dataOriginal, filtroDepartamento]);

  // Códigos SIRR únicos según el municipio y/o departamento seleccionado
  const codigosSIRRFiltrados = useMemo(() => {
    const features = dataOriginal?.features || [];
    return [
      ...new Set(
        features
          .filter(
            (f) =>
              (!filtroDepartamento ||
                f.properties.departamento === filtroDepartamento) &&
              (!filtroMunicipio || f.properties.municipio === filtroMunicipio)
          )
          .map((f) => f.properties.codigoSIRR)
          .filter(Boolean)
      ),
    ];
  }, [dataOriginal, filtroDepartamento, filtroMunicipio]);

  //Numero visita únicos según el municipio y/o departamento /o codigo sirr seleccionado
  // Cambia el filtro de visitas para aceptar múltiples valores
  const numerovisitaFiltrados = useMemo(() => {
    const features = dataOriginal?.features || [];
    return [
      ...new Set(
        features
          .filter(
            (f) =>
              (!filtroDepartamento ||
                f.properties.departamento === filtroDepartamento) &&
              (!filtroMunicipio ||
                f.properties.municipio === filtroMunicipio) &&
              (!filtroCodigoSIRR ||
                f.properties.codigoSIRR === filtroCodigoSIRR)
          )
          .map((f) => f.properties.numero_visita)
          .filter(Boolean)
      ),
    ];
  }, [dataOriginal, filtroDepartamento, filtroMunicipio, filtroCodigoSIRR]);

  // Modifica el filtrado de datos para aceptar múltiples visitas
  useEffect(() => {
    if (!dataOriginal) return;

    const features = dataOriginal.features || [];

    const filtradas = features.filter((f) => {
      const p = f.properties;

      return (
        (filtroDepartamento === "" || p.departamento === filtroDepartamento) &&
        (filtroMunicipio === "" || p.municipio === filtroMunicipio) &&
        (filtroCodigoSIRR === "" || p.codigoSIRR === filtroCodigoSIRR) &&
        (filtroNumerovisita.length === 0 ||
          filtroNumerovisita.includes(p.numero_visita))
      );
    });

    // Actualizar el estado con misma estructura FeatureCollection
    setDataVisitasTecnicasGeovisor({
      type: "FeatureCollection",
      features: filtradas,
    });
  }, [
    filtroDepartamento,
    filtroMunicipio,
    filtroCodigoSIRR,
    filtroNumerovisita,
    dataOriginal,
  ]);

  useEffect(() => {
    setLoading(true);
    const fetchDataVisitasTecnicas = async () => {
      try {
        const dataVisitas = await getDataGeovisorArn();

        setDataVisitasTecnicasGeovisor(dataVisitas?.data);
        setDataOriginal(dataVisitas?.data);

        setLoading(false);
      } catch (err) {
        console.error("Error al obtener data:", err);
      }
    };

    fetchDataVisitasTecnicas();
  }, []);

  useEffect(() => {
    if (dataVisitasTecnicasGeovisor && dataVisitasTecnicasGeovisor.features) {
      const features = dataVisitasTecnicasGeovisor.features;

      setDepartamentosUnicos([
        ...new Set(
          features.map((f) => f.properties.departamento).filter(Boolean)
        ),
      ]);
      setMunicipiosUnicos([
        ...new Set(features.map((f) => f.properties.municipio).filter(Boolean)),
      ]);
      setCodigosSIRRUnicos([
        ...new Set(
          features.map((f) => f.properties.codigoSIRR).filter(Boolean)
        ),
      ]);

      setNumeroVisitaUnicas([
        ...new Set(
          features.map((f) => f.properties.numero_visita).filter(Boolean)
        ),
      ]);
    } else {
      setDepartamentosUnicos([]);
      setMunicipiosUnicos([]);
      setCodigosSIRRUnicos([]);
      setNumeroVisitaUnicas([]);
    }
  }, [dataOriginal]);

  // Asegúrate de tener al menos 15 colores
  const availableColors = ["green"];

  const hayFiltros = !!(
    filtroDepartamento ||
    filtroMunicipio ||
    filtroCodigoSIRR ||
    filtroNumerovisita.length > 0
  );

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "#f5f5f5",
        width: "100%",
      }}
    >
      {loading ? (
        <div
          style={{
            minHeight: "100vh",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" tip="Cargando datos..." />
        </div>
      ) : (
        <>
          {/* Sidebar izquierdo */}
          <div
            style={{
              width: 350,
              minWidth: 280,
              maxWidth: 400,
              background: "#fff",
              borderRight: "1px solid #e5e7eb",
              padding: 24,
              display: "flex",
              flexDirection: "column",
              gap: 24,
              boxShadow: "2px 0 8px 0 rgba(0,0,0,0.03)",
            }}
          >
            {/* Header Section */}
            <div>
              <h2
                style={{
                  margin: 0,
                  color: "#1f2937",
                  fontSize: "28px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                Geovisor - ARN
              </h2>
              <p
                style={{
                  margin: 0,
                  color: "#6b7280",
                  fontSize: "16px",
                }}
              ></p>
            </div>

            {/* Filtros */}
            <Card
              style={{
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                margin: 0,
              }}
              bodyStyle={{ padding: "16px" }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "#374151",
                  fontSize: "18px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "16px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "4px",
                    height: "20px",
                    backgroundColor: "#3b82f6",
                    borderRadius: "2px",
                  }}
                ></span>
                Filtros de Búsqueda
              </h3>
              <Form layout="vertical">
                <Row gutter={[0, 8]}>
                  <Col span={24}>
                    <Form.Item
                      label={
                        <span style={{ fontWeight: "500", color: "#374151" }}>
                          Departamento
                        </span>
                      }
                      style={{ marginBottom: "8px" }}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Seleccionar departamento"
                        value={filtroDepartamento || undefined}
                        onChange={(value) => {
                          setFiltroDepartamento(value || "");
                          setFiltroMunicipio("");
                          setFiltroCodigoSIRR("");
                        }}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{ borderRadius: "8px", height: "40px" }}
                      >
                        {departamentosUnicos.map((dep) => (
                          <Select.Option key={dep} value={dep}>
                            {dep}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={
                        <span style={{ fontWeight: "500", color: "#374151" }}>
                          Municipio
                        </span>
                      }
                      style={{ marginBottom: "8px" }}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Seleccionar municipio"
                        value={filtroMunicipio || undefined}
                        onChange={(value) => {
                          setFiltroMunicipio(value || "");
                          setFiltroCodigoSIRR("");
                        }}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{ borderRadius: "8px", height: "40px" }}
                      >
                        {municipiosFiltrados.map((mun) => (
                          <Select.Option key={mun} value={mun}>
                            {mun}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={
                        <span style={{ fontWeight: "500", color: "#374151" }}>
                          Código SIRR
                        </span>
                      }
                      style={{ marginBottom: "8px" }}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Seleccionar código SIRR"
                        value={filtroCodigoSIRR || undefined}
                        onChange={(value) => setFiltroCodigoSIRR(value || "")}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{ borderRadius: "8px", height: "40px" }}
                      >
                        {codigosSIRRFiltrados.map((cod) => (
                          <Select.Option key={cod} value={cod}>
                            {cod}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Button
                      onClick={() => {
                        setFiltroDepartamento("");
                        setFiltroMunicipio("");
                        setFiltroCodigoSIRR("");
                        setFiltroNumerovisita([]);
                      }}
                      style={{ width: "100%", marginTop: 8 }}
                      type="default"
                      icon={<ReloadOutlined />}
                      disabled={!hayFiltros}
                    >
                      Limpiar Filtros
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card>
          </div>

          {/* Panel derecho: el mapa */}
          <div style={{ flex: 1, padding: 24, minWidth: 0 }}>
            <Card
              style={{
                borderRadius: "12px",
                overflow: "auto",
                boxShadow:
                  "0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e5e7eb",
                height: "100%",
              }}
              bodyStyle={{ padding: 0, height: "100%", borderRadius: "12px" }}
            >
              <MapContainer
                bounds={colombiaBounds}
                maxBounds={colombiaBounds}
                maxBoundsViscosity={1}
                minZoom={6}
                zoom={6}
                style={{
                  height: "100%",
                  width: "100%",
                  borderRadius: "12px",
                }}
                worldCopyJump={false}
              >
                <LayersControl position="topright">
                  <BaseLayer checked name="Mapa Base - Colombia">
                    <TileLayer
                      attribution="&copy; OpenStreetMap contributors"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      noWrap
                    />
                  </BaseLayer>

                  {dataVisitasTecnicasGeovisor && (
                    <Overlay key="visitas" name="📍 Visitas Técnicas" checked>
                      <GeoJSON
                        data={dataVisitasTecnicasGeovisor}
                        key={`geojson-${dataVisitasTecnicasGeovisor.features.length}`}
                        style={() => styleVisitasPatti}
                        pointToLayer={(feature, latlng) => {
                          const numVisita = feature.properties.numero_visita;
                          // Usa el color fijo según el número de visita (por defecto rojo si no está en el mapa)
                          const color =
                            colorPorNumeroVisita[numVisita] || "green";
                          return L.marker(latlng, {
                            icon: MarcadorPersonalizado(color),
                          });
                        }}
                        onEachFeature={(feature, layer) => {
                          const p = feature.properties;
                          const fecha = p.fecha_registro;
                          const html = `
                              <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 13px; line-height: 1.4; max-width: 300px;">

                                <div style="background-color: #f9fafb; padding: 10px; border-radius: 6px; margin-bottom: 12px;">
                                  <div style="font-weight: 600; color: #374151; margin-bottom: 6px;">🎯 Información del Proyecto</div>
                                  <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">PREPP: <span style="color: #1f2937; font-weight: 500;">${p.codigoPREPP}</span></div>
                                  <div style="font-size: 12px; color: #6b7280;">Proyecto: <span style="color: #1f2937; font-weight: 500;">${p.nombreProyecto}</span></div>
                                                                  <div style="margin-bottom: 12px;">
                                  <div style="display: flex; align-items: center; margin-bottom: 6px;">
                                    <span style="color: #6b7280; font-weight: 500; min-width: 80px;">Ubicación:</span>
                                    <span style="color: #1f2937;">${p.departamento}, ${p.municipio}</span>
                                  </div>
                                  <div style="display: flex; align-items: center; margin-bottom: 6px;">
                                    <span style="color: #6b7280; font-weight: 500; min-width: 80px;">🌱 Vereda:</span>
                                    <span style="color: #1f2937;">${p.vereda}</span>
                                  </div>
                                </div>
                                </div>

                                <div style="margin-bottom: 12px;">
                                  <div style="display: flex; align-items: center; margin-bottom: 6px;">
                                    <span style="color: #6b7280; font-weight: 500; min-width: 100px;">👤 Participante:</span>
                                    <span style="color: #1f2937;">${p.nombreParticipante}</span>
                                  </div>
                                  <div style="display: flex; align-items: center; margin-bottom: 6px;">
                                    <span style="color: #6b7280; font-weight: 500; min-width: 100px;">🆔 Código SIRR:</span>
                                    <span style="color: #1f2937; font-weight: 600;">${p.codigoSIRR}</span>
                                  </div>
                                  <div style="display: flex; align-items: center; margin-bottom: 6px;">
                                    <span style="color: #6b7280; font-weight: 500; min-width: 100px;">🌾 Línea Prod.:</span>
                                    <span style="color: #1f2937;">${p.linea_productiva}</span>
                                  </div>
                                </div>
                              </div>
                            `;

                          layer.bindPopup(html, {
                            maxHeight: 400,
                            maxWidth: 350,
                            autoPan: true,
                            autoPanPadding: [20, 20],
                            className: "custom-popup",
                          });
                        }}
                      />
                    </Overlay>
                  )}

                  <LayersControl.Overlay
                    key="consejosComunitarios"
                    name="Consejos comunitarios titulado"
                    checked={false}
                  >
                    <LayerGroup
                      eventHandlers={{
                        add: () =>
                          cargarCapa(
                            "consejosComunitarios",
                            "/capas/Consejo_Comunitario_Titulado.geojson"
                          ),
                      }}
                    >
                      {capas["consejosComunitarios"] && (
                        <GeoJSON
                          data={capas["consejosComunitarios"]}
                          style={() => styleconsejosComunitarios}
                          onEachFeature={(feature, layer) => {
                            const p = feature.properties;
                            const html = `
              <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 320px;">
                <div style="background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); color: white; padding: 10px 16px; border-radius: 8px 8px 0 0;">
                  <h4 style="margin: 0; font-size: 18px; font-weight: 700;">🟢 Consejo Comunitario Titulado</h4>
                </div>
                <div style="padding: 12px; background: #f9fafb; border-radius: 0 0 8px 8px;">
                  <div><strong>Nombre:</strong> ${p.NOMBRE || "—"}</div>
                </div>
              </div>
            `;
                            layer.bindPopup(html);
                          }}
                        />
                      )}
                    </LayerGroup>
                  </LayersControl.Overlay>

                  <LayersControl.Overlay
                    key="zonaReservaCampesinaConstituida"
                    name="Zonas de reserva campesina constituidas"
                    checked={false}
                  >
                    <LayerGroup
                      eventHandlers={{
                        add: () =>
                          cargarCapa(
                            "zonaReservaCampesinaConstituida",
                            "/capas/Zonas_de_Reserva_Campesina_Constituida.geojson"
                          ),
                      }}
                    >
                      {capas["zonaReservaCampesinaConstituida"] && (
                        <GeoJSON
                          data={capas["zonaReservaCampesinaConstituida"]}
                          style={() => stylezonaReservaCampesinaConstituida}
                          onEachFeature={(feature, layer) => {
                            const p = feature.properties;
                            const html = `
            <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 320px;">
              <div style="background: linear-gradient(135deg, #f59e42 0%, #fbbf24 100%); color: white; padding: 10px 16px; border-radius: 8px 8px 0 0;">
                <h4 style="margin: 0; font-size: 18px; font-weight: 700;">🟠 Zona de Reserva Campesina</h4>
              </div>
              <div style="padding: 12px; background: #f9fafb; border-radius: 0 0 8px 8px;">
                <div><strong>Nombre:</strong> ${p.NOMBRE_ZON || "—"}</div>
                <div><strong>Resolución:</strong> ${p.RESOLUCION || "—"}</div>
              </div>
            </div>
          `;
                            layer.bindPopup(html);
                          }}
                        />
                      )}
                    </LayerGroup>
                  </LayersControl.Overlay>

                  <LayersControl.Overlay
                    key="resguardoIndigenaFormalizado"
                    name="Resguardos indígenas formalizados"
                    checked={false}
                  >
                    <LayerGroup
                      eventHandlers={{
                        add: () =>
                          cargarCapa(
                            "resguardoIndigenaFormalizado",
                            "/capas/Resguardo_Indigena_Formalizado.geojson"
                          ),
                      }}
                    >
                      {capas["resguardoIndigenaFormalizado"] && (
                        <GeoJSON
                          data={capas["resguardoIndigenaFormalizado"]}
                          style={() => styleresguardoIndigenaFormalizado}
                          onEachFeature={(feature, layer) => {
                            const p = feature.properties;
                            const html = `
            <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 320px;">
              <div style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; padding: 10px 16px; border-radius: 8px 8px 0 0;">
                <h4 style="margin: 0; font-size: 18px; font-weight: 700;">🔵 Resguardo Indígena</h4>
              </div>
              <div style="padding: 12px; background: #f9fafb; border-radius: 0 0 8px 8px;">
                <div><strong>Nombre:</strong> ${p.NOMBRE || "—"}</div>
                <div><strong>Área (ha):</strong> ${
                  p.AREA_TOTAL_ACTOS_ADMIN
                    ? Number(p.AREA_TOTAL_ACTOS_ADMIN).toLocaleString()
                    : "—"
                }</div>
              </div>
            </div>
          `;
                            layer.bindPopup(html);
                          }}
                        />
                      )}
                    </LayerGroup>
                  </LayersControl.Overlay>

                  <LayersControl.Overlay
                    key="reservaForestalNacional"
                    name="Reservas forestales nacionales"
                    checked={false}
                  >
                    <LayerGroup
                      eventHandlers={{
                        add: () =>
                          cargarCapa(
                            "reservaForestalNacional",
                            "/capas/Reserva_Forestal_Nacional_329062953925962160.geojson"
                          ),
                      }}
                    >
                      {capas["reservaForestalNacional"] && (
                        <GeoJSON
                          data={capas["reservaForestalNacional"]}
                          style={() => stylereservaForestalNacional}
                          pointToLayer={(feature, latlng) => {
                            const customIcon = L.icon({
                              iconUrl:
                                "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
                              iconSize: [25, 41],
                              iconAnchor: [12, 41],
                              popupAnchor: [1, -34],
                              shadowUrl:
                                "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
                              shadowSize: [41, 41],
                            });
                            return L.marker(latlng, { icon: customIcon });
                          }}
                          onEachFeature={(feature, layer) => {
                            const p = feature.properties;
                            const html = `
            <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 320px;">
              <div style="background: linear-gradient(135deg, #14532d 0%, #14532d 100%); color: white; padding: 10px 16px; border-radius: 8px 8px 0 0;">
                <h4 style="margin: 0; font-size: 18px; font-weight: 700;">🌲 Reserva forestal nacional</h4>
              </div>
              <div style="padding: 12px; background: #f9fafb; border-radius: 0 0 8px 8px;">
                <!-- Aquí puedes agregar más info si quieres -->
              </div>
            </div>
          `;
                            layer.bindPopup(html);
                          }}
                        />
                      )}
                    </LayerGroup>
                  </LayersControl.Overlay>

                  <LayersControl.Overlay
                    key="runap"
                    name="RUNAP"
                    checked={false}
                  >
                    <LayerGroup
                      eventHandlers={{
                        add: () => cargarCapa("runap", "/capas/runap.geojson"),
                      }}
                    >
                      {capas["runap"] && (
                        <GeoJSON
                          data={capas["runap"]}
                          style={() => stylerunap}
                          pointToLayer={(feature, latlng) => {
                            const customIcon = L.icon({
                              iconUrl:
                                "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
                              iconSize: [25, 41],
                              iconAnchor: [12, 41],
                              popupAnchor: [1, -34],
                              shadowUrl:
                                "https://unpkg.com/leaflet@1.9.3/dist/images/leaflet-shadow.png",
                              shadowSize: [41, 41],
                            });
                            return L.marker(latlng, { icon: customIcon });
                          }}
                          onEachFeature={(feature, layer) => {
                            const p = feature.properties;
                            const html = `
            <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 320px;">
              <div style="background: linear-gradient(135deg, #8b5cf6 0%, #a21caf 100%); color: white; padding: 10px 16px; border-radius: 8px 8px 0 0;">
                <h4 style="margin: 0; font-size: 18px; font-weight: 700;">🟣 RUNAP</h4>
              </div>
              <div style="padding: 12px; background: #f9fafb; border-radius: 0 0 8px 8px;">
                <div><strong>Nombre:</strong> ${p.ap_nombre || "—"}</div>
                <div><strong>Área total (ha):</strong> ${
                  p.area_ha_to ? Number(p.area_ha_to).toLocaleString() : "—"
                }</div>
              </div>
            </div>
          `;
                            layer.bindPopup(html);
                          }}
                        />
                      )}
                    </LayerGroup>
                  </LayersControl.Overlay>

                  <LayersControl.Overlay
                    key="desagregacionRegional"
                    name="Desagregación regional"
                    checked={false}
                  >
                    <LayerGroup
                      eventHandlers={{
                        add: () =>
                          cargarCapa(
                            "desagregacionRegional",
                            "/capas/desagregacion_region.geojson"
                          ),
                      }}
                    >
                      {capas["desagregacionRegional"] && (
                        <GeoJSON
                          data={capas["desagregacionRegional"]}
                          style={() => styledesagregacionRegional}
                          pointToLayer={(feature, latlng) => {
                            const customIcon = L.icon({
                              iconUrl:
                                "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
                              iconSize: [25, 41],
                              iconAnchor: [12, 41],
                              popupAnchor: [1, -34],
                              shadowUrl:
                                "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
                              shadowSize: [41, 41],
                            });
                            return L.marker(latlng, { icon: customIcon });
                          }}
                          onEachFeature={(feature, layer) => {
                            const p = feature.properties;
                            const html = `
            <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 320px;">
              <div style="background: linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%); color: white; padding: 10px 16px; border-radius: 8px 8px 0 0;">
                <h4 style="margin: 0; font-size: 18px; font-weight: 700;">🔷 Desagregación regional</h4>
              </div>
              <div style="padding: 12px; background: #f9fafb; border-radius: 0 0 8px 8px;">
                <!-- Aquí puedes agregar los campos que quieras mostrar -->
              </div>
            </div>
          `;
                            layer.bindPopup(html);
                          }}
                        />
                      )}
                    </LayerGroup>
                  </LayersControl.Overlay>
                </LayersControl>

                {mostrarSostenibilidad && sostenibilidad && (
                  <GeoJSON
                    data={sostenibilidad}
                    pointToLayer={(feature, latlng) => {
                      return L.marker(latlng, { icon: trianguloIcono });
                    }}
                    onEachFeature={(feature, layer) => {
                      const p = feature.properties;
                      const html = `
          <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 320px;">
            <div style="background: linear-gradient(135deg, #328BC7 0%, #328BC7 100%); color: #f9fafb; padding: 10px 16px; border-radius: 8px 8px 0 0;">
              <h4 style="margin: 0; font-size: 18px; font-weight: 700;">🌱 Sostenibilidad</h4>
            </div>
            <div style="padding: 12px; background: #f9fafb; border-radius: 0 0 8px 8px;">
              <div><strong>Proyecto:</strong> ${p.nombre}</div>
              <div><strong>Actividad productiva:</strong> ${p.actividadProductiva}</div>
              <div><strong>sector:</strong> ${p.Sector}</div>
              <div><strong>Coordenadas (latitud, longitud):</strong> ${p.latitud}, ${p.longitud} </div>
            </div>
          </div>
        `;
                      layer.bindPopup(html);
                    }}
                    checked
                  />
                )}
              </MapContainer>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
